import React, { useState, useEffect, useRef, useCallback } from 'react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { Sparkles, Loader2, X, RotateCw } from 'lucide-react';
import { Button } from './ui/button';

const AI_STORE_KEY = 'explorer-ai-search-v1';

export function AiDealSearch({ onResults, getFilters }) {
  const [desc, setDesc] = useState('');
  const [terms, setTerms] = useState([]);
  const [termInput, setTermInput] = useState('');
  const [termsEdited, setTermsEdited] = useState(false);
  const [status, setStatus] = useState(null); // null | running | done | failed | interrupted
  const [progress, setProgress] = useState({ scored: 0, total: 0 });
  const [count, setCount] = useState(0);
  const [starting, setStarting] = useState(false);
  const pollRef = useRef(null);
  const runIdRef = useRef(null);

  const persist = (obj) => {
    try { sessionStorage.setItem(AI_STORE_KEY, JSON.stringify(obj)); } catch { /* ignore */ }
  };

  const poll = useCallback((runId) => {
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const { data: d } = await api.aiSearchStatus(runId);
        setStatus(d.status);
        setProgress({ scored: d.scored, total: d.total });
        setCount((d.results || []).length);
        if (d.terms?.length) setTerms(prev => (prev.length ? prev : d.terms));
        onResults(d.results || [], { status: d.status, runId });
        if (d.status !== 'running' && d.status !== 'interpreting') {
          clearInterval(pollRef.current);
          if (d.status === 'done') toast.success(`KI-Suche fertig — ${(d.results || []).length} bewertete Deals`);
          else toast.error(d.error || 'KI-Suche unterbrochen — bitte erneut starten');
        }
      } catch { /* keep polling */ }
    }, 2500);
  }, [onResults]);

  useEffect(() => {
    let saved = null;
    try { saved = JSON.parse(sessionStorage.getItem(AI_STORE_KEY) || 'null'); } catch { /* ignore */ }
    if (saved?.runId) {
      setDesc(saved.description || '');
      setTerms(saved.terms || []);
      runIdRef.current = saved.runId;
      api.aiSearchStatus(saved.runId).then(({ data: d }) => {
        setStatus(d.status);
        setProgress({ scored: d.scored, total: d.total });
        setCount((d.results || []).length);
        if (d.terms?.length) setTerms(d.terms);
        onResults(d.results || [], { status: d.status, runId: saved.runId });
        if (d.status === 'running' || d.status === 'interpreting') poll(saved.runId);
      }).catch(() => {
        try { sessionStorage.removeItem(AI_STORE_KEY); } catch { /* ignore */ }
        onResults(null, {});
      });
    } else if (saved?.description) {
      setDesc(saved.description);
    }
    return () => clearInterval(pollRef.current);
    // eslint-disable-next-line
  }, []);

  const start = async (withTerms) => {
    const d = desc.trim();
    if (d.length < 10) { toast.error('Bitte beschreibe das gesuchte Geschäft etwas genauer'); return; }
    setStarting(true);
    try {
      const useTerms = withTerms && withTerms.length ? withTerms : undefined;
      const res = await api.aiSearchStart({
        description: d, terms: useTerms,
        filters: getFilters ? getFilters() : undefined,
      });
      if (!useTerms) setTerms([]);
      setTermsEdited(false);
      runIdRef.current = res.data.run_id;
      setCount(0);
      setProgress({ scored: 0, total: 0 });
      setStatus(res.data.status);
      persist({ description: d, terms: res.data.terms || [], runId: res.data.run_id });
      onResults([], { status: res.data.status, runId: res.data.run_id });
      poll(res.data.run_id);
    } catch (e) { toast.error(e.response?.data?.detail || 'KI-Suche fehlgeschlagen'); }
    setStarting(false);
  };

  const clear = () => {
    clearInterval(pollRef.current);
    runIdRef.current = null;
    setStatus(null); setTerms([]); setCount(0); setTermsEdited(false);
    try { sessionStorage.removeItem(AI_STORE_KEY); } catch { /* ignore */ }
    onResults(null, {});
  };

  const removeTerm = (t) => { setTerms(prev => prev.filter(x => x !== t)); setTermsEdited(true); };
  const addTerm = () => {
    const parts = termInput.split(',').map(s => s.trim().toLowerCase()).filter(Boolean);
    if (!parts.length) { setTermInput(''); return; }
    setTerms(prev => {
      const next = [...prev];
      parts.forEach(p => { if (!next.includes(p)) next.push(p); });
      return next.slice(0, 20);
    });
    setTermsEdited(true);
    setTermInput('');
  };

  const running = status === 'running' || status === 'interpreting';
  const pct = progress.total ? Math.round((progress.scored / progress.total) * 100) : 0;

  return (
    <div className="border border-sky-500/25 bg-sky-500/[0.04] rounded-sm p-3 space-y-2.5" data-testid="ai-search-panel">
      <p className="text-[10px] uppercase tracking-widest font-bold text-sky-300/80 flex items-center gap-1.5">
        <Sparkles className="w-3 h-3" /> KI-Suche — Geschäft beschreiben
      </p>
      <div className="flex gap-2 items-stretch">
        <textarea rows={2} value={desc} onChange={e => setDesc(e.target.value)} data-testid="ai-search-input"
          placeholder='Beschreibe das gesuchte Geschäft… z. B. „Zerspanungs-/CNC-Fertiger mit eigener Fertigung, B2B, Nachfolge wegen Alter"'
          className="flex-1 bg-secondary border border-border rounded-sm p-2 text-xs text-foreground resize-none focus:outline-none focus:border-sky-500/50 placeholder:text-muted-foreground/50" />
        <Button onClick={() => start()} disabled={starting || running} data-testid="ai-search-start-btn"
          className="bg-sky-600 hover:bg-sky-500 text-white rounded-sm h-auto px-4 text-xs shrink-0">
          {(starting || running) ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Sparkles className="w-3.5 h-3.5 mr-1.5" />}
          KI-Suche
        </Button>
      </div>

      {terms.length > 0 && (
        <div className="space-y-1">
          <p className="text-[10px] text-muted-foreground">KI-Interpretation — Suchbegriffe für die Vorauswahl (anpassbar):</p>
          <div className="flex flex-wrap items-center gap-1 border border-sky-500/30 rounded-sm px-1.5 py-1 bg-secondary">
            {terms.map(t => (
              <span key={t} className="flex items-center gap-1 text-[11px] rounded px-1.5 py-0.5 bg-sky-500/15 text-sky-300" data-testid="ai-search-term-chip">
                {t}
                <button onClick={() => removeTerm(t)} disabled={running} className="opacity-70 hover:opacity-100 disabled:opacity-30" data-testid={`ai-search-term-remove-${t}`}>
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
            <input value={termInput} onChange={e => setTermInput(e.target.value)} data-testid="ai-search-term-input"
              onKeyDown={e => { if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); addTerm(); } }}
              onBlur={addTerm} disabled={running}
              placeholder="Begriff + Enter…"
              className="flex-1 min-w-[100px] bg-transparent text-xs text-foreground outline-none h-6 px-1 placeholder:text-muted-foreground/40" />
          </div>
        </div>
      )}

      {running && (
        <div className="space-y-1" data-testid="ai-search-progress">
          <div className="flex items-center justify-between text-[11px] text-sky-300">
            <span className="flex items-center gap-1.5">
              <Loader2 className="w-3 h-3 animate-spin" />
              {status === 'interpreting'
                ? 'KI interpretiert deine Beschreibung…'
                : `KI bewertet Kandidaten… ${progress.scored}/${progress.total}`}
            </span>
            {status === 'running' && <span>{count} Treffer bisher</span>}
          </div>
          <div className="h-1.5 bg-sky-500/10 rounded-full overflow-hidden">
            <div className={`h-full bg-sky-500 transition-all duration-500 ${status === 'interpreting' ? 'animate-pulse' : ''}`}
              style={{ width: status === 'interpreting' ? '15%' : `${pct}%` }} />
          </div>
        </div>
      )}

      {status && !running && (
        <div className="flex items-center justify-between flex-wrap gap-2 text-[11px]">
          <span className={status === 'done' ? 'text-emerald-400' : 'text-red-400'} data-testid="ai-search-status">
            {status === 'done'
              ? `${count} Deals bewertet — sortiert nach Match-Score`
              : 'Suche unterbrochen/fehlgeschlagen'}
          </span>
          <div className="flex items-center gap-2">
            {(termsEdited || status !== 'done') && (
              <button onClick={() => start(terms)} disabled={starting} data-testid="ai-search-rerun-btn"
                className="inline-flex items-center gap-1 text-sky-300 border border-sky-500/40 hover:bg-sky-500/10 rounded-sm px-2 py-1 transition-colors">
                <RotateCw className="w-3 h-3" /> {termsEdited ? 'Mit angepassten Begriffen neu bewerten' : 'Erneut versuchen'}
              </button>
            )}
            <button onClick={clear} data-testid="ai-search-clear-btn"
              className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground border border-border rounded-sm px-2 py-1 transition-colors">
              <X className="w-3 h-3" /> KI-Suche beenden
            </button>
          </div>
        </div>
      )}
      {running && (
        <div className="flex justify-end">
          <button onClick={clear} data-testid="ai-search-clear-btn"
            className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground border border-border rounded-sm px-2 py-1 transition-colors">
            <X className="w-3 h-3" /> Abbrechen & zurück zur normalen Suche
          </button>
        </div>
      )}
    </div>
  );
}
